import { Component, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import { PayrollService } from 'src/app/core/services/payroll.service';
import { ReimbursementService } from 'src/app/core/services/reimbursement.service';
import { downloadFile } from 'src/app/helpers/utils';
import { PayPeriodModel } from 'src/app/models/payPeriodModel';
import {
  ReimbursementCampusModel,
  ReimbursementManagementFilters,
  ReimbursementModel,
  ReimbursementStatus
} from 'src/app/models/reimbursementModel';
import { AlertService } from 'src/app/shared/services/alert.service';

@Component({
  selector: 'app-reimbursement-approval',
  templateUrl: './reimbursement-approval.component.html',
  standalone: false
})
export class ReimbursementApprovalComponent implements OnInit {
  reimbursements: ReimbursementModel[] = [];
  payPeriods: PayPeriodModel[] = [];
  campuses: ReimbursementCampusModel[] = [];
  approvedAmounts: { [id: number]: number } = {};
  selectedPayPeriods: { [id: number]: number } = {};
  denialReasons: { [id: number]: string } = {};
  processingID: number = null;
  loading = true;
  filters: ReimbursementManagementFilters = this.defaultFilters();
  reimbursementStatus = ReimbursementStatus;

  constructor(
    private reimbursementService: ReimbursementService,
    private payrollService: PayrollService,
    private alertService: AlertService) {
  }

  ngOnInit(): void {
    this.loading = true;
    forkJoin({
      reimbursements: this.reimbursementService.getManagementReimbursements(this.filters),
      payPeriods: this.payrollService.loadPayrollPeriods(),
      campuses: this.reimbursementService.getManagementCampuses()
    }).subscribe({
      next: result => {
        this.payPeriods = result.payPeriods;
        this.campuses = result.campuses;
        this.setReimbursements(result.reimbursements);
      },
      error: error => {
        this.loading = false;
        this.alertService.error(this.errorMessage(error, 'Unable to load reimbursement requests.'));
      }
    });
  }

  loadReimbursements(): void {
    this.loading = true;
    this.reimbursementService.getManagementReimbursements(this.filters).subscribe({
      next: reimbursements => this.setReimbursements(reimbursements),
      error: error => {
        this.loading = false;
        this.alertService.error(this.errorMessage(error, 'Unable to load reimbursement requests.'));
      }
    });
  }

  clearFilters(): void {
    this.filters = this.defaultFilters();
    this.loadReimbursements();
  }

  approve(reimbursement: ReimbursementModel): void {
    const approvedAmount = this.approvedAmounts[reimbursement.id];
    const payPeriodID = this.selectedPayPeriods[reimbursement.id];
    if (!approvedAmount || approvedAmount <= 0 || !payPeriodID) {
      this.alertService.error('Enter an approved amount greater than zero and select a payroll period.');
      return;
    }

    this.processingID = reimbursement.id;
    this.reimbursementService.approve(reimbursement.id, approvedAmount, payPeriodID).subscribe({
      next: () => {
        this.reimbursements = this.reimbursements.filter(item => item.id !== reimbursement.id);
        this.processingID = null;
        this.alertService.success('Reimbursement approved for payroll.');
      },
      error: error => {
        this.processingID = null;
        this.alertService.error(this.errorMessage(error, 'Unable to approve the reimbursement.'));
      }
    });
  }

  deny(reimbursement: ReimbursementModel): void {
    const denialReason = this.denialReasons[reimbursement.id]?.trim();
    if (!denialReason) {
      this.alertService.error('A denial reason is required.');
      return;
    }

    this.processingID = reimbursement.id;
    this.reimbursementService.deny(reimbursement.id, denialReason).subscribe({
      next: () => {
        this.reimbursements = this.reimbursements.filter(item => item.id !== reimbursement.id);
        this.processingID = null;
        this.alertService.success('Reimbursement denied and returned to the employee.');
      },
      error: error => {
        this.processingID = null;
        this.alertService.error(this.errorMessage(error, 'Unable to deny the reimbursement.'));
      }
    });
  }

  downloadReceipt(reimbursement: ReimbursementModel): void {
    this.reimbursementService.downloadReceipt(reimbursement.receiptAttachmentID).subscribe({
      next: file => downloadFile(file, reimbursement.receiptFileName, true),
      error: error => this.alertService.error(this.errorMessage(error, 'Unable to download the receipt.'))
    });
  }

  private setReimbursements(reimbursements: ReimbursementModel[]): void {
    this.reimbursements = reimbursements;
    reimbursements.forEach(reimbursement => {
      this.approvedAmounts[reimbursement.id] = reimbursement.approvedAmount ?? reimbursement.requestedAmount;
      this.selectedPayPeriods[reimbursement.id] = reimbursement.payPeriodID;
      this.denialReasons[reimbursement.id] = reimbursement.denialReason || '';
    });
    this.loading = false;
  }

  private errorMessage(error: any, fallback: string): string {
    return typeof error?.error === 'string' ? error.error : fallback;
  }

  private defaultFilters(): ReimbursementManagementFilters {
    return {
      statusID: ReimbursementStatus.Pending,
      employee: '',
      campusID: null,
      minimumAmount: null,
      maximumAmount: null,
      submittedFrom: '',
      submittedThrough: '',
      payPeriodID: null
    };
  }
}
